// Contenu du camion le long de sa journée, pour l'infobulle des segments du Gantt et sa vue agrandie.
// Le stock suit la règle du serveur (compartmentStockByStep). Ce module le traduit en tonnes par compartiment,
// relie un segment du Gantt à ses étapes et minute la lecture : trajets rapides, chargements et livraisons
// au rythme des compartiments. La ligne du temps va de 0 à 100 : la veille (Chargé PM, sans heure) au début,
// puis la journée à l'échelle des heures.
import { compartmentStockByStep } from "./compartmentStock.js";
import { isTruckDompeurType } from "./fleetReassignmentHelpers.js";

export const TRUCK_LOAD_VEILLE_SHARE = 9;
const TRAVEL_PERCENT_PER_SECOND = 30;
const OPERATION_LEAD_SECONDS = 0.45;
const SECONDS_PER_COMPARTMENT = 0.3;
// Le dompeur charge ou lève sa benne en une fois : assez long pour voir la benne monter, verser et redescendre.
const DUMP_OPERATION_SECONDS = 2.4;
const EPSILON_TONS = 0.005;

const text = (value) => String(value ?? "").trim();
const toNumber = (value) => (value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const ease = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
const cloneFills = (fills) => Object.fromEntries(Object.entries(fills).map(([pos, layers]) => [pos, layers.map((layer) => ({ ...layer }))]));
const tonsOf = (fills, pos, order) => fills[pos]?.find((layer) => layer.order === order)?.tons || 0;

function setLayerTons(fills, pos, order, tons) {
  const layers = fills[pos] || [];
  const layer = layers.find((item) => item.order === order);
  if (tons <= EPSILON_TONS) {
    const rest = layers.filter((item) => item !== layer);
    if (rest.length) fills[pos] = rest; else delete fills[pos];
    return;
  }
  if (layer) layer.tons = tons; else fills[pos] = [...layers, { order, tons }];
}

// Une étape fixe la quantité de chacune de ses commandes dans ses compartiments.
const applyEntry = (fills, entry) => entry.items.forEach((item) => setLayerTons(fills, item.pos, item.order, tonsOf(entry.after, item.pos, item.order)));

// Contenu avant (ou après, avec including) une étape, dans l'ordre de lecture des blocs.
function fillsAroundEntry(timeline, entryIndex, { including = false } = {}) {
  const block = timeline.blocks.find((candidate) => candidate.entries.includes(entryIndex));
  const fills = cloneFills(block.startFills);
  for (const index of block.entries) {
    if (index === entryIndex && !including) break;
    applyEntry(fills, timeline.entries[index]);
    if (index === entryIndex) break;
  }
  return fills;
}

const cache = new WeakMap();

// null quand le camion n'a ni compartiments connus ni chargement ou livraison.
// Un dompeur n'a pas de compartiments : sa benne est un seul compartiment de la capacité du camion.
export function buildTruckLoadTimeline(assignment) {
  if (!assignment || typeof assignment !== "object") return null;
  if (!cache.has(assignment)) cache.set(assignment, computeTruckLoadTimeline(assignment));
  return cache.get(assignment);
}

function computeTruckLoadTimeline(assignment) {
  const compartments = (Array.isArray(assignment.truck_compartments) ? assignment.truck_compartments : [])
    .map((compartment) => ({ id: text(compartment?.compartment_id), pos: Number(compartment?.position), cap: Number(compartment?.capacity_tons) }))
    .filter((compartment) => compartment.pos > 0 && compartment.cap > 0)
    .sort((left, right) => left.pos - right.pos);
  const dump = !compartments.length && toNumber(assignment.capacity_tons) > 0 && isTruckDompeurType(assignment);
  if (dump) compartments.push({ id: "", pos: 1, cap: toNumber(assignment.capacity_tons) });
  const steps = Array.isArray(assignment.operational_steps) ? assignment.operational_steps : [];
  const stock = compartmentStockByStep(steps);
  if (!compartments.length || !stock.length) return null;

  const posById = new Map(compartments.map((compartment) => [compartment.id, compartment.pos]));
  const productByOrder = new Map();
  steps.forEach((step) => (Array.isArray(step?.compartment_allocations) ? step.compartment_allocations : []).forEach((allocation) => {
    const id = text(allocation?.compartment_id);
    const position = dump ? 1 : Number(allocation?.position);
    if (id && !posById.has(id) && position > 0) posById.set(id, position);
    const order = text(allocation?.order_id) || text(step.order_id);
    const product = text(allocation?.grain_type) || text(step.grain_type);
    if (order && product && !productByOrder.has(order)) productByOrder.set(order, product);
  }));
  const fillsOf = (stockMap) => {
    const fills = {};
    stockMap.forEach((orders, id) => {
      const pos = posById.get(id);
      if (pos) fills[pos] = [...orders].map(([order, tons]) => ({ order, tons }));
    });
    return fills;
  };

  const entries = stock.map(({ step, before, after }) => {
    const kind = step.step_type === "loading" ? "load" : "deliv";
    const beforeFills = fillsOf(before);
    const afterFills = fillsOf(after);
    // Compartiments touchés, dans l'ordre des allocations ; sinon tous ceux dont le contenu change.
    const items = [];
    const addItem = (pos, order) => {
      if (pos && order && !items.some((item) => item.pos === pos && item.order === order)) items.push({ pos, order });
    };
    (Array.isArray(step.compartment_allocations) ? step.compartment_allocations : []).forEach((allocation) => {
      addItem(posById.get(text(allocation?.compartment_id)), text(allocation?.order_id) || text(step.order_id));
    });
    if (!items.length) {
      compartments.forEach(({ pos }) => {
        const orders = new Set([...(beforeFills[pos] || []), ...(afterFills[pos] || [])].map((layer) => layer.order));
        orders.forEach((order) => { if (Math.abs(tonsOf(beforeFills, pos, order) - tonsOf(afterFills, pos, order)) > EPSILON_TONS) addItem(pos, order); });
      });
    }
    const startMinutes = toNumber(step.display_arrival_minutes) ?? toNumber(step.display_start_minutes);
    const endMinutes = toNumber(step.display_service_end_minutes) ?? toNumber(step.display_departure_minutes) ?? toNumber(step.display_end_minutes);
    const orderId = text(step.order_id);
    return {
      kind,
      stepId: text(step.step_id),
      deliveryId: text(step.delivery_id),
      orderId,
      orderIds: [...new Set([orderId, ...items.map((item) => item.order)].filter(Boolean))],
      label: text(step.label) || text(step.client_name),
      tons: toNumber(step.quantity_tons) ?? items.reduce((sum, item) => sum + Math.abs(tonsOf(afterFills, item.pos, item.order) - tonsOf(beforeFills, item.pos, item.order)), 0),
      sameSiteKey: text(step.same_site_group_key),
      before: beforeFills,
      after: afterFills,
      items,
      startMinutes,
      endMinutes: endMinutes ?? startMinutes,
      veille: startMinutes == null,
    };
  });

  // Blocs de la ligne du temps : les étapes consécutives au même arrêt, ou sur la même plage horaire, n'en font qu'un.
  // La veille (sans heure), les chargements d'un même site sont réunis, comme les groupes « Chargé PM » du Gantt.
  const blocks = [];
  entries.forEach((entry, index) => {
    const last = entry.veille
      ? blocks.find((block) => block.veille && block.kind === entry.kind && block.entries.some((other) => (
        entries[other].label === entry.label || (entry.sameSiteKey && entries[other].sameSiteKey === entry.sameSiteKey))))
      : blocks[blocks.length - 1];
    const lastEntry = last ? entries[last.entries[last.entries.length - 1]] : null;
    const sameStop = lastEntry && lastEntry.kind === entry.kind && lastEntry.veille === entry.veille && (
      entry.veille
      || (entry.sameSiteKey && entry.sameSiteKey === lastEntry.sameSiteKey)
      || (lastEntry.startMinutes === entry.startMinutes && lastEntry.endMinutes === entry.endMinutes)
    );
    if (sameStop) {
      last.entries.push(index);
      if (entry.label && !last.label.split(" · ").includes(entry.label)) last.label = [last.label, entry.label].filter(Boolean).join(" · ");
    } else {
      blocks.push({ entries: [index], kind: entry.kind, label: entry.label, veille: entry.veille, startMinutes: entry.startMinutes, endMinutes: entry.endMinutes });
    }
  });
  // Contenu au début de chaque bloc, dans l'ordre de lecture (la veille est réordonnée par site).
  let running = {};
  blocks.forEach((block) => {
    block.startFills = running;
    running = cloneFills(running);
    block.entries.forEach((index) => applyEntry(running, entries[index]));
    block.itemCount = block.entries.reduce((sum, index) => sum + entries[index].items.length, 0);
    block.endMinutes = Math.max(...block.entries.map((index) => entries[index].endMinutes ?? block.startMinutes ?? 0));
    block.tons = block.entries.reduce((sum, index) => sum + (entries[index].tons || 0), 0);
    block.orderIds = [...new Set(block.entries.flatMap((index) => entries[index].orderIds))];
  });

  const dayBlocks = blocks.filter((block) => !block.veille);
  const veilleBlocks = blocks.filter((block) => block.veille);
  const firstMinutes = dayBlocks.length ? Math.min(...dayBlocks.map((block) => block.startMinutes)) : 360;
  const lastMinutes = dayBlocks.length ? Math.max(...dayBlocks.map((block) => block.endMinutes)) : firstMinutes + 60;
  const dayStart = Math.min(toNumber(assignment.display_start_minutes) ?? firstMinutes - 60, firstMinutes);
  const dayEnd = Math.max(toNumber(assignment.display_end_minutes) ?? lastMinutes + 30, lastMinutes + 1);
  const veilleShare = veilleBlocks.length ? TRUCK_LOAD_VEILLE_SHARE : 0;
  const percentOfMinutes = (minutes) => veilleShare + ((minutes - dayStart) / (dayEnd - dayStart)) * (100 - veilleShare);
  veilleBlocks.forEach((block, index) => {
    const width = veilleShare / veilleBlocks.length;
    block.x0 = index * width + 0.2;
    block.x1 = (index + 1) * width - 0.2;
  });
  // Jamais de chevauchement : la lecture avance toujours vers la droite.
  dayBlocks.forEach((block, index) => {
    block.x0 = Math.max(percentOfMinutes(block.startMinutes), index ? dayBlocks[index - 1].x1 : veilleShare);
    block.x1 = Math.max(percentOfMinutes(block.endMinutes), block.x0 + 0.4);
  });
  // Étendue affichée d'un bloc : l'opération puis le trajet jusqu'au bloc suivant, comme les segments du Gantt.
  blocks.forEach((block, index) => {
    const next = blocks[index + 1];
    block.laneX1 = block.veille ? block.x1 : Math.max(block.x1, next && !next.veille ? next.x0 - 0.2 : Math.min(100, block.x1 + 1.5));
  });

  // Minutage de la lecture : parties « trajet » et « opération » qui couvrent toute la ligne.
  const parts = [];
  let x = 0;
  blocks.forEach((block, index) => {
    if (block.x0 > x) parts.push({ kind: "travel", x0: x, x1: block.x0, duration: (block.x0 - x) / TRAVEL_PERCENT_PER_SECOND });
    parts.push({ kind: "op", block: index, x0: block.x0, x1: block.x1, duration: dump ? DUMP_OPERATION_SECONDS : OPERATION_LEAD_SECONDS + block.itemCount * SECONDS_PER_COMPARTMENT });
    x = block.x1;
  });
  if (x < 100) parts.push({ kind: "travel", x0: x, x1: 100, duration: (100 - x) / TRAVEL_PERCENT_PER_SECOND });
  let time = 0;
  parts.forEach((part) => { part.t0 = time; time += part.duration; part.t1 = time; });

  const hourTicks = [];
  for (let hour = Math.ceil(dayStart / 60); hour * 60 <= dayEnd; hour += 2) hourTicks.push({ minutes: hour * 60, x: percentOfMinutes(hour * 60) });

  return {
    compartments,
    dump,
    capacityTons: toNumber(assignment.capacity_tons) || compartments.reduce((sum, compartment) => sum + compartment.cap, 0),
    entries,
    blocks,
    parts,
    duration: time,
    productByOrder,
    veilleShare,
    dayStart,
    dayEnd,
    hourTicks,
  };
}

// Étapes du camion couvertes par les segments survolés (un segment ou les membres d'un groupe).
// Chargement : par l'étape de chargement des allocations ; livraison : par la livraison ; sinon par commande.
export function findTruckLoadEntries(timeline, segments = []) {
  if (!timeline) return [];
  const keys = new Set();
  const fallbackKeys = new Set();
  (Array.isArray(segments) ? segments : []).filter(Boolean).forEach((segment) => {
    const stopType = segment.stopType;
    (Array.isArray(segment.compartmentAllocations) ? segment.compartmentAllocations : []).forEach((allocation) => {
      if (stopType === "loading" && text(allocation?.loading_step_id)) keys.add(`load:${text(allocation.loading_step_id)}`);
      if (stopType === "delivery" && text(allocation?.delivery_id)) keys.add(`deliv:${text(allocation.delivery_id)}`);
    });
    const orderId = text(segment.operationOrderId) || text(segment.orderLabel);
    if (orderId && (stopType === "loading" || stopType === "delivery")) fallbackKeys.add(`${stopType === "loading" ? "load" : "deliv"}:${orderId}`);
  });
  return timeline.entries.reduce((found, entry, index) => {
    const key = `${entry.kind}:${entry.kind === "load" ? entry.stepId : entry.deliveryId}`;
    if (keys.has(key) || (!keys.size && fallbackKeys.has(`${entry.kind}:${entry.orderId}`))) found.push(index);
    return found;
  }, []);
}

export const truckLoadTons = (fills) => Object.values(fills).reduce((sum, layers) => sum + layers.reduce((total, layer) => total + layer.tons, 0), 0);

// Bilan après les étapes : tonnes à bord, compartiments occupés et libres.
export function summarizeTruckLoad(timeline, fills) {
  const used = timeline.compartments.filter(({ pos }) => fills[pos]?.length).map(({ pos }) => pos);
  return {
    tons: Math.round(truckLoadTons(fills) * 10) / 10,
    used,
    free: timeline.compartments.filter(({ pos }) => !used.includes(pos)),
  };
}

// Dernière des étapes données dans l'ordre de lecture.
export function truckLoadLastEntry(timeline, entryIndexes = []) {
  const order = timeline.blocks.flatMap((block) => block.entries);
  return entryIndexes.filter((index) => timeline.entries[index]).sort((left, right) => order.indexOf(left) - order.indexOf(right)).pop();
}

// État après les étapes données : compartiments de ces étapes encadrés, et leurs commandes.
export function truckLoadMarkedState(timeline, entryIndexes = []) {
  const indexes = entryIndexes.filter((index) => timeline.entries[index]);
  const lastIndex = truckLoadLastEntry(timeline, indexes);
  return {
    fills: fillsAroundEntry(timeline, lastIndex, { including: true }),
    kind: timeline.entries[lastIndex].kind,
    hot: [...new Set(indexes.flatMap((index) => timeline.entries[index].items.map((item) => item.pos)))],
    orders: [...new Set(indexes.flatMap((index) => timeline.entries[index].orderIds))],
  };
}

// État pendant une étape (avancement 0 → 1) : les compartiments se remplissent ou se vident un à un.
// unload : avancement de la livraison du compartiment en cours (la benne d'un dompeur se lève).
export function truckLoadEntryState(timeline, entryIndex, progress) {
  const entry = timeline.entries[entryIndex];
  const count = entry.items.length;
  if (progress >= 1 || !count) return { fills: fillsAroundEntry(timeline, entryIndex, { including: progress >= 1 }), lids: {}, hot: [] };
  const before = fillsAroundEntry(timeline, entryIndex);
  const fills = cloneFills(before);
  const state = { fills, lids: {}, hot: [] };
  if (progress <= 0) return state;
  const current = Math.min(count - 1, Math.floor(progress * count));
  const local = progress * count - current;
  entry.items.forEach((item, index) => {
    if (index > current) return;
    const from = tonsOf(before, item.pos, item.order);
    const to = tonsOf(entry.after, item.pos, item.order);
    const share = index < current ? 1 : entry.kind === "load" ? ease(clamp01((local - 0.3) / 0.6)) : ease(clamp01(local / 0.85));
    setLayerTons(fills, item.pos, item.order, from + (to - from) * share);
  });
  const item = entry.items[current];
  state.hot = [item.pos];
  if (entry.kind === "load") {
    state.lids[item.pos] = local < 0.2 ? local / 0.2 : local < 0.9 ? 1 : (1 - local) / 0.1;
    state.spout = {
      pos: item.pos,
      fromPos: entry.items[Math.max(0, current - 1)].pos,
      move: current === 0 ? 1 : ease(clamp01(local / 0.3)),
      alpha: current === 0 ? clamp01(local / 0.2) : 1,
      flow: local > 0.3 && local < 0.9,
      order: item.order,
    };
  } else {
    state.unload = { progress: local, order: item.order };
    if (local < 0.85) state.drain = { pos: item.pos, order: item.order };
  }
  return state;
}

// Lecture à l'instant time (secondes) : position sur la ligne, bloc en cours et état du camion.
export function truckLoadTimelineAt(timeline, time) {
  const t = Math.max(0, Math.min(timeline.duration, time));
  // À la fin exacte d'une opération, on reste sur elle : c'est son bilan qui s'affiche.
  const part = timeline.parts.find((candidate) => t < candidate.t1 || (candidate.kind === "op" && t <= candidate.t1 + 1e-9))
    || timeline.parts[timeline.parts.length - 1];
  const progress = part.duration ? clamp01((t - part.t0) / part.duration) : 1;
  const x = part.x0 + (part.x1 - part.x0) * progress;
  if (part.kind === "op") {
    const block = timeline.blocks[part.block];
    if (progress >= 1 - 1e-6) {
      return { x, time: t, block: part.block, inOperation: false, ended: true, state: truckLoadMarkedState(timeline, block.entries) };
    }
    // Les étapes du bloc passent l'une après l'autre, chacune au prorata de ses compartiments.
    let remaining = progress * Math.max(block.itemCount, 1);
    for (const index of block.entries) {
      const count = Math.max(timeline.entries[index].items.length, block.itemCount ? 0 : 1);
      if (remaining < count || index === block.entries[block.entries.length - 1]) {
        return { x, time: t, block: part.block, inOperation: true, ended: false, state: { ...truckLoadEntryState(timeline, index, count ? remaining / count : 1), kind: "play" } };
      }
      remaining -= count;
    }
  }
  const previous = [...timeline.parts].reverse().find((candidate) => candidate.kind === "op" && candidate.t1 <= t + 1e-9);
  const lastEntry = previous ? timeline.blocks[previous.block].entries.slice(-1)[0] : -1;
  const fills = lastEntry >= 0 ? fillsAroundEntry(timeline, lastEntry, { including: true }) : {};
  const next = timeline.parts.find((candidate) => candidate.kind === "op" && candidate.t0 >= t - 1e-9);
  return { x, time: t, block: previous ? previous.block : -1, nextBlock: next ? next.block : -1, inOperation: false, ended: false, state: { fills, lids: {}, hot: [], kind: "play" } };
}

// Instant de lecture pour une position de la ligne (glisser) et parties d'opération d'un bloc.
export function truckLoadTimeAtPercent(timeline, percent) {
  const x = Math.max(0, Math.min(100, percent));
  const part = timeline.parts.find((candidate) => x <= candidate.x1) || timeline.parts[timeline.parts.length - 1];
  return part.t0 + part.duration * clamp01((x - part.x0) / ((part.x1 - part.x0) || 1));
}

export const truckLoadOperationPart = (timeline, blockIndex) => timeline.parts.find((part) => part.kind === "op" && part.block === blockIndex) || null;
export const truckLoadBlockOfEntry = (timeline, entryIndex) => timeline.blocks.findIndex((block) => block.entries.includes(entryIndex));

// Heure affichée pour une position de la ligne ; null dans la zone de la veille.
export function truckLoadMinutesAtPercent(timeline, percent) {
  if (percent < timeline.veilleShare) return null;
  return timeline.dayStart + ((percent - timeline.veilleShare) / (100 - timeline.veilleShare)) * (timeline.dayEnd - timeline.dayStart);
}
